import { Box, Grid, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useCallback, useEffect, useState } from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'
import {
  generateTrackingLink,
  getDecodedTrackingUrlForClickStream,
} from '../../../utils/brandsHelper'
import axios from '../../axios'
import { useAppState } from '../../appState'
import { COLORS, CSEvents, ENDPOINTS, FONTS } from '../../constants'
import { tracker } from '../../systemLogs'
import CHROutlinedButton from '../outlinedButton'
import CHRSpinner from '../spinner'
import { styles as proTabSharedStyles } from './styles'
import BrandTooltip from '../brandTooltip'

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(1),
  },
  grid: {
    marginTop: theme.spacing(2),
    width: '100%',
  },
  row: {
    fontFamily: FONTS.Graphik.GraphikRegular,
    fontSize: '1rem',
    fontWeight: 400,
    textTransform: 'lowercase',
    color: COLORS.black,
    margin: 0,
    lineHeight: 1.5,
  },
  brandLink: {
    color: COLORS.black,
    textDecoration: 'none',
    display: 'block',
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.88,
    },
  },
  commissionSpan: {
    fontFamily: FONTS.Graphik.GraphikLight,
    fontSize: '0.8rem',
    fontWeight: 700,
    color: COLORS.coolTeal,
    cursor: 'inherit',
    whiteSpace: 'nowrap',
  },
  subtitle: {
    marginTop: theme.spacing(1),
    fontFamily: FONTS.Graphik.GraphikLight,
    fontSize: '0.95rem',
    color: COLORS.darkGrey,
  },
  empty: {
    fontFamily: FONTS.Graphik.GraphikLight,
    fontSize: '0.95rem',
    color: COLORS.darkGrey,
    marginTop: theme.spacing(2),
  },
}))

export type NewBrandRow = {
  id: number
  brandId: string
  brandName: string
  /** Split-adjusted rate for display, or `''` when cashback is not shown for this brand. */
  commission: string
  network: string
  url: string
  trackingLink: string
}

/** Formats commission for display and tooltip — mirrors brands list parentheses style. */
export function formatNewBrandCommission(commission: string): string {
  if (!commission || !commission.trim()) return ''
  const c = commission.trim()
  if (c.toLowerCase().includes('fixed')) {
    const num = parseFloat(c.replace(/fixed/gi, '').trim())
    if (Number.isNaN(num)) return `(${c})`
    return `(Fixed $${num.toFixed(2)})`
  }
  return `(${c})`
}

type Props = WithTranslation

const NewBrandsSection = ({ t }: Props) => {
  const classes = useStyles()
  const shared = proTabSharedStyles()
  const [appState] = useAppState()
  const { userProfile: { userName } = {} } = appState
  const userId = appState.userId

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [brands, setBrands] = useState<NewBrandRow[]>([])

  const loadBrands = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const { data: envelope } = await axios.get(
        ENDPOINTS.newBrandsLast30Days,
        {
          params: { days: '30' },
        }
      )
      const payload = envelope?.data
      const rows: NewBrandRow[] = Array.isArray(payload)
        ? [...(payload as NewBrandRow[])]
        : []
      rows.sort((a: NewBrandRow, b: NewBrandRow) =>
        a.brandName.localeCompare(b.brandName, undefined, {
          sensitivity: 'base',
        })
      )
      setBrands(rows)
    } catch {
      setError(t('messages.somethingWentWrong'))
      setBrands([])
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    loadBrands()
  }, [loadBrands])

  const parsedUserId =
    userId != null && userId !== '' ? Number(userId) : Number.NaN
  const resolvedUserId = Number.isNaN(parsedUserId) ? undefined : parsedUserId

  return (
    <div className={classes.root}>
      <Typography variant="h1">new brands</Typography>
      <Typography variant="body1" className={classes.subtitle}>
        retailers added in the last 30 days
      </Typography>

      {loading && (
        <Box mt={3} display="flex" justifyContent="center">
          <CHRSpinner />
        </Box>
      )}

      {!loading && error && (
        <Box
          className={shared.sectionErrorBanner}
          mt={2}
          role="alert"
          aria-live="polite"
        >
          <Typography component="p" className={shared.sectionErrorTitle}>
            {t('chirpyestPro.sectionLoadErrorTitle')}
          </Typography>
          <Typography component="p" className={shared.sectionErrorBody}>
            {t('chirpyestPro.sectionLoadErrorHint')}
          </Typography>
          <div className={shared.sectionErrorActions}>
            <CHROutlinedButton
              label={t('chirpyestPro.sectionRetry')}
              font={FONTS.Graphik.GraphikMedium}
              onClick={loadBrands}
            />
          </div>
        </Box>
      )}

      {!loading && !error && brands.length === 0 && (
        <p className={classes.empty}>{t('chirpyestPro.newBrandsEmpty')}</p>
      )}

      {!loading && !error && brands.length > 0 && (
        <Grid container spacing={2} className={classes.grid}>
          {brands.map(brand => {
            const customText = 'cash back'
            const commissionDisplay = formatNewBrandCommission(brand.commission)
            const showsCashbackRate = Boolean(brand.commission?.trim())

            const trackingItem = {
              ...brand,
              commission: brand.commission,
              isCashbackShownInWebsite: showsCashbackRate,
            }

            const trackingUrl = generateTrackingLink(
              trackingItem,
              {
                userId: resolvedUserId,
                userName,
              },
              '',
              '',
              Boolean(userId)
            )

            return (
              <Grid
                item
                xs={12}
                md={4}
                lg={3}
                xl={3}
                key={`${brand.brandId}-${brand.id}`}
              >
                <h3 className={classes.row}>
                  <a
                    href={trackingUrl}
                    className={classes.brandLink}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => {
                      tracker.trackCs(CSEvents.clickBrand, {
                        pageUrl: window.location.href,
                        brandId: brand.brandId,
                        section: 'chirpyest-pro-new-brands',
                        trackingUrl: getDecodedTrackingUrlForClickStream(
                          trackingUrl
                        ),
                      })
                    }}
                  >
                    <span>{brand.brandName}</span>
                    {commissionDisplay ? (
                      <BrandTooltip
                        cashback={commissionDisplay}
                        customText={customText}
                        commissionException=""
                      >
                        <span className={classes.commissionSpan}>
                          {' '}
                          {commissionDisplay}
                        </span>
                      </BrandTooltip>
                    ) : null}
                  </a>
                </h3>
              </Grid>
            )
          })}
        </Grid>
      )}
    </div>
  )
}

export default withTranslation()(NewBrandsSection)
